import { emptyDiscount, eventTotals } from './totals';

/**
 * Varianti del preventivo (stessa regola del backend, `src/event/quote-variants.ts`):
 * alternative tra cui la cliente sceglie. Cliente, data e luogo sono in comune;
 * cambiano servizi, prova, trasferta, sconto, acconto e una nota per la cliente.
 *
 * La variante attiva vive nei campi di sempre dell'evento (`event.services`,
 * `event.test_cost`, `financial`): tutta la scheda la modifica senza saperlo.
 * Cambiare variante salva i campi in quella attiva e carica l'altra.
 */
export interface QuoteVariant {
  id: string;
  name: string;
  note?: string;
  services?: any[];
  test_cost?: number;
  travel_cost?: number;
  discount?: any;
  deposit?: { cost?: number; type?: string };
}

const clone = <T>(v: T): T => (v == null ? v : JSON.parse(JSON.stringify(v)));

export function variantList(event: any): QuoteVariant[] {
  return event?.event?.quote_variants ?? [];
}

/** Più di una variante: altrimenti il preventivo è uno solo, come sempre. */
export function hasVariants(event: any): boolean {
  return variantList(event).length > 1;
}

export function activeVariantId(event: any): string | null {
  const list = variantList(event);
  return event?.event?.quote_active_variant ?? list[0]?.id ?? null;
}

/** Prima di salvare: la variante attiva riprende i dati della scheda. */
export function syncActiveVariant(event: any): void {
  if (hasVariants(event)) captureActive(event);
}

/** Copia i campi dell'evento nella variante attiva. */
function captureActive(event: any): void {
  const v = variantList(event).find((x) => x.id === activeVariantId(event));
  if (!v) return;
  const e = event.event ?? {};
  const f = event.financial ?? {};
  v.services = clone(e.services ?? []);
  v.test_cost = +e.test_cost || 0;
  v.travel_cost = +f.travel_fee?.cost || 0;
  v.discount = clone(f.discount ?? null);
  v.deposit = { cost: +f.deposit?.cost || 0, type: f.deposit?.type || 'fixed' };
}

/** Porta i dati della variante nei campi dell'evento (pagato/note dell'acconto restano). */
function loadInto(event: any, v: QuoteVariant): void {
  event.event.services = clone(v.services ?? []);
  event.event.test_cost = +(v.test_cost ?? 0) || 0;
  event.financial = event.financial ?? {};
  event.financial.travel_fee = { ...(event.financial.travel_fee ?? {}), cost: +(v.travel_cost ?? 0) || 0 };
  event.financial.discount = clone(v.discount) ?? emptyDiscount();
  event.financial.deposit = {
    ...(event.financial.deposit ?? {}),
    cost: +(v.deposit?.cost ?? 0) || 0,
    type: v.deposit?.type || 'fixed',
  };
  event.event.quote_active_variant = v.id;
}

export function switchVariant(event: any, id: string): void {
  if (id === activeVariantId(event)) return;
  const target = variantList(event).find((x) => x.id === id);
  if (!target) return;
  captureActive(event);
  loadInto(event, target);
}

/**
 * Aggiunge una variante copiando quella attiva e la rende attiva. La prima
 * volta il preventivo di sempre diventa la prima variante.
 */
export function addVariant(event: any, firstName: string, newName: string): void {
  const list = variantList(event);
  if (!list.length) {
    list.push({ id: newId(), name: firstName });
    event.event.quote_variants = list;
    event.event.quote_active_variant = list[0].id;
  }
  captureActive(event);
  const current = list.find((x) => x.id === activeVariantId(event))!;
  const copy: QuoteVariant = { ...clone(current), id: newId(), name: newName, note: '' };
  list.push(copy);
  event.event.quote_active_variant = copy.id;
}

/** Elimina una variante; con una sola rimasta il preventivo torna semplice. */
export function removeVariant(event: any, id: string): void {
  const list = variantList(event);
  if (id === activeVariantId(event)) {
    const other = list.find((x) => x.id !== id);
    if (other) {
      captureActive(event);
      loadInto(event, other);
    }
  }
  const rest = list.filter((x) => x.id !== id);
  if (rest.length < 2) {
    delete event.event.quote_variants;
    delete event.event.quote_active_variant;
  } else {
    event.event.quote_variants = rest;
  }
}

/** L'evento come sarebbe con questa variante; per l'attiva l'evento stesso. */
export function variantEvent(event: any, v: QuoteVariant): any {
  if (v.id === activeVariantId(event)) return event;
  const f = event?.financial ?? {};
  return {
    ...event,
    event: { ...(event?.event ?? {}), services: v.services ?? [], test_cost: +(v.test_cost ?? 0) || 0 },
    financial: {
      ...f,
      travel_fee: { ...(f.travel_fee ?? {}), cost: +(v.travel_cost ?? 0) || 0 },
      discount: v.discount ?? null,
      deposit: { ...(f.deposit ?? {}), cost: +(v.deposit?.cost ?? 0) || 0, type: v.deposit?.type || 'fixed' },
    },
  };
}

export function variantTotal(event: any, v: QuoteVariant): number {
  return eventTotals(variantEvent(event, v)).total;
}

function newId(): string {
  return Math.random().toString(36).slice(2, 10);
}
